import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';

@Component({
	selector: 'app-camera-upload',
	templateUrl: './camera-upload.component.html',
	styleUrls: ['./camera-upload.component.scss']
})
export class CameraUploadComponent implements OnInit, OnDestroy {
	@ViewChild('video', { static: true }) video: ElementRef<HTMLVideoElement>;  // Reference to the video element
	selectedFile: File | null = null;
	imagePreview: string | ArrayBuffer | null = null;

	/** Form Group */
	dialogForm: FormGroup;

	private mediaStream: MediaStream | null = null;

	videoDevices: MediaDeviceInfo[] = [];
	currentDeviceId: string | null = null;

	currentDeviceIndex: number = 0;

	constructor(private http: HttpClient,
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<CameraUploadComponent>,
		public dialog: MatDialog,) { }

	ngOnInit(): void {
		this.getVideoDevices();
		this.startCamera();

		this.dialogForm = this.fb.group({
		});

	}

	// Get video devices
	async getVideoDevices() {
		const devices = await navigator.mediaDevices.enumerateDevices();
		this.videoDevices = devices.filter(device => device.kind === 'videoinput');

		// If there are devices available, set the current device ID
		if (this.videoDevices.length > 0) {
			this.currentDeviceId = this.videoDevices[0].deviceId; // Default to the first camera
		}
	}

	// Start camera with the current device ID
	async startCamera() {
		// Stop the current camera before switching to a new one
		this.stopCamera();

		const currentDeviceId = this.videoDevices[this.currentDeviceIndex]?.deviceId;

		const constraints = {
			video: { deviceId: currentDeviceId ? { exact: currentDeviceId } : undefined }
		};

		try {
			const stream = await navigator.mediaDevices.getUserMedia(constraints);
			this.mediaStream = stream;
			this.video.nativeElement.srcObject = stream;
		} catch (error) {
			console.error('Error accessing the camera: ', error);
		}
	}

	switchCamera() {
		if (this.videoDevices.length === 0) return; // No cameras available

		// Increment the index and wrap around if needed
		this.currentDeviceIndex = (this.currentDeviceIndex + 1) % this.videoDevices.length;
		this.startCamera(); // Restart the camera with the new device ID
	}

	// Stop the webcam stream
	stopCamera(): void {
		if (this.mediaStream) {
			const tracks = this.mediaStream.getTracks();
			tracks.forEach(track => track.stop()); // Stop all tracks (audio/video)
			this.video.nativeElement.srcObject = null; // Clear the video element source
			this.mediaStream = null; // Clear the media stream reference
		}
	}

	// Capture the current frame from the video feed
	captureImage(): void {
		const videoElement = this.video.nativeElement;

		// Create a canvas to capture the video frame
		const canvas = document.createElement('canvas');
		canvas.width = videoElement.videoWidth;
		canvas.height = videoElement.videoHeight;

		const ctx = canvas.getContext('2d');
		if (ctx) {
			ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
			// Convert the canvas image to a data URL (image preview)
			this.imagePreview = canvas.toDataURL('image/png');

			// Convert the data URL to a Blob (image file) for upload
			canvas.toBlob(blob => {
				if (blob) {
					this.selectedFile = new File([blob], 'captured-image.png', { type: 'image/png' });
					this.uploadImage();
				}
			}, 'image/png');
		}
	}

	// Upload the captured image
	uploadImage(): void {
		let dialogData: any = {}
		dialogData['file'] = this.selectedFile

		this.dialogRef.close(dialogData);
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		this.dialogRef.close();
	}

	// Clean up when the component is destroyed
	ngOnDestroy(): void {
		this.stopCamera();  // Stop the camera when the component is destroyed
	}
}
